/**
 * Shared admin API fetch helper — session token, host query, install bootstrap.
 */

import { Redirect } from "@shopify/app-bridge/actions";
import {
  getShopifySessionToken,
  getCachedHost,
  getCachedShop,
  getAppBridgeInstance,
  initializeAppBridge,
  normalizeShopifyHostParam,
  readPersistedEmbeddedParams,
} from "../utils/app-bridge";

const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || "";

export const SHOP_NOT_INSTALLED_CODE = "SHOP_NOT_INSTALLED" as const;

/** Thrown when OAuth install redirect is triggered; pages can ignore in UI. */
export class ShopInstallRedirectError extends Error {
  constructor() {
    super("Shop installation required — redirecting");
    this.name = "ShopInstallRedirectError";
  }
}

/** Use in page-level catch blocks to skip banners/alerts while OAuth install redirect runs. */
export function isShopInstallRedirectError(err: unknown): err is ShopInstallRedirectError {
  return err instanceof ShopInstallRedirectError;
}

let installRedirectScheduled = false;

function triggerOAuthInstallRedirect(shop: string): void {
  const url = new URL("/auth", window.location.origin);
  url.searchParams.set("shop", shop);
  const href = url.toString();

  const app = getAppBridgeInstance();
  if (app) {
    const redirect = Redirect.create(app);
    redirect.dispatch(Redirect.Action.REMOTE, {
      url: href,
      newContext: true,
    });
  } else {
    window.location.assign(href);
  }
}

async function waitForAppBridge(maxWaitMs = 10000): Promise<void> {
  // App.tsx may skip initializeAppBridge when isEmbedded() is false (e.g. cached host
  // cleared after client navigation) while the iframe URL still has shop+host — init here.
  if (!getAppBridgeInstance()) {
    try {
      const params = new URLSearchParams(window.location.search);
      let shop = params.get("shop");
      const hasHostInUrl = Boolean(
        normalizeShopifyHostParam(params.get("host"))
      );
      const inIframe =
        typeof window !== "undefined" && window.self !== window.top;
      const persisted = readPersistedEmbeddedParams();
      if (!shop) {
        shop = persisted.shop;
      }
      const persistedHost = normalizeShopifyHostParam(persisted.host);
      const hasHost = hasHostInUrl || Boolean(persistedHost);
      if (shop && (hasHost || inIframe)) {
        initializeAppBridge();
      }
    } catch {
      /* App.tsx or retry loop below */
    }
  }

  const startTime = Date.now();
  while (!getAppBridgeInstance()) {
    if (Date.now() - startTime > maxWaitMs) {
      throw new Error("Timeout waiting for App Bridge initialization");
    }
    await new Promise((resolve) => setTimeout(resolve, 100));
  }
}

/**
 * Authenticated admin API request. On SHOP_NOT_INSTALLED, redirects to /auth once (deduped).
 */
export async function adminApiRequest<T>(
  endpoint: string,
  options: RequestInit = {}
): Promise<T> {
  if (installRedirectScheduled) {
    throw new ShopInstallRedirectError();
  }

  await waitForAppBridge();

  const sessionToken = await getShopifySessionToken();
  const hostParam = getCachedHost();

  let fullEndpoint = `${API_BASE_URL}${endpoint}`;
  if (hostParam) {
    const separator = endpoint.includes("?") ? "&" : "?";
    fullEndpoint += `${separator}host=${encodeURIComponent(hostParam)}`;
  }

  const response = await fetch(fullEndpoint, {
    ...options,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${sessionToken}`,
      ...options.headers,
    },
  });

  if (response.status === 401) {
    const body = (await response.json().catch(() => ({}))) as {
      code?: string;
      error?: string;
      message?: string;
    };
    if (body.code === SHOP_NOT_INSTALLED_CODE) {
      const shop = getCachedShop();
      if (!shop) {
        throw new Error(
          "Shop not installed — open this app from Shopify Admin to complete installation."
        );
      }
      if (!installRedirectScheduled) {
        installRedirectScheduled = true;
        triggerOAuthInstallRedirect(shop);
      }
      throw new ShopInstallRedirectError();
    }
    const msg = body.error || body.message || "API request failed";
    throw new Error(msg);
  }

  if (!response.ok) {
    const error = (await response.json().catch(() => ({
      error: "Unknown error",
    }))) as { error?: string; message?: string };
    throw new Error(error.error || error.message || "API request failed");
  }

  if (response.status === 204) {
    return {} as T;
  }

  return response.json() as Promise<T>;
}
